import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, BellOff, BellRing, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { notifyAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState('loading'); // loading | done | resubscribed | invalid
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!token) {
      setState('invalid');
      return;
    }
    // Clicking the unsubscribe link expresses intent — opt out immediately.
    notifyAPI
      .unsubscribe(token)
      .then((res) => {
        if (!alive) return;
        setEmail(res.data?.email || '');
        setState('done');
      })
      .catch(() => {
        if (alive) setState('invalid');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const handleResubscribe = useCallback(async () => {
    setBusy(true);
    try {
      const res = await notifyAPI.resubscribe(token);
      setEmail(res.data?.email || email);
      setState('resubscribed');
      toast.success('You are subscribed to order-window alerts again.');
    } catch (err) {
      toast.error('Could not update your preference. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [token, email]);

  if (state === 'loading') {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="unsubscribe-loading">
        <Loader2 className="h-8 w-8 text-teal animate-spin mx-auto" />
      </div>
    );
  }

  if (state === 'invalid') {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="unsubscribe-invalid">
        <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-4" strokeWidth={1.5} />
        <h1 className="font-heading text-2xl text-foreground mb-2">Invalid unsubscribe link</h1>
        <p className="font-body text-sm text-foreground/60 mb-6">
          This link is missing or has an invalid token. You can manage alert
          preferences from your profile instead.
        </p>
        <Link to="/profile">
          <Button variant="ctaPrimary" className="rounded-full font-body text-sm font-bold px-6" data-testid="unsubscribe-goto-profile">
            Go to my profile
          </Button>
        </Link>
      </div>
    );
  }

  const resubscribed = state === 'resubscribed';

  return (
    <div data-testid="unsubscribe-page" className="max-w-md mx-auto px-4 py-16 sm:py-24">
      <div className="p-8 rounded-2xl bg-card border border-border text-center">
        <div className={`w-14 h-14 rounded-full ${resubscribed ? 'bg-teal/15' : 'bg-gold/15'} flex items-center justify-center mx-auto mb-5`}>
          {resubscribed ? (
            <BellRing className="h-7 w-7 text-teal" />
          ) : (
            <BellOff className="h-7 w-7 text-gold" />
          )}
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-light text-foreground tracking-tight mb-2">
          {resubscribed ? "You're subscribed again" : 'You have been unsubscribed'}
        </h1>
        <p className="font-body text-sm text-foreground/70 mb-1">
          {resubscribed
            ? 'You will keep receiving order-window alerts.'
            : "You won't receive order-window or cart reminder emails anymore."}
        </p>
        {email && (
          <p className="font-body text-xs text-foreground/45 mb-6" data-testid="unsubscribe-email">
            {email}
          </p>
        )}

        {!resubscribed && (
          <Button
            onClick={handleResubscribe}
            disabled={busy}
            variant="ctaSecondary"
            className="rounded-full font-body text-sm font-bold px-6 disabled:opacity-50"
            data-testid="unsubscribe-resubscribe-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Changed your mind? Re-subscribe'}
          </Button>
        )}

        <div className="mt-6">
          <Link
            to="/profile"
            className="font-body text-xs text-foreground/50 underline hover:text-foreground/80"
            data-testid="unsubscribe-manage-link"
          >
            Manage all preferences in your profile
          </Link>
        </div>
      </div>
    </div>
  );
}
